(() => {
  if (customElements.get('netum-resource-library')) return;
  const normalize = value => String(value || '').normalize('NFKD').toLowerCase().replace(/\p{M}/gu, '').replace(/[^\p{L}\p{N}]/gu, '');
  const platformOf = value => {
    const text = String(value || '').toLowerCase();
    if (/windows|win\d/.test(text)) return 'windows';
    if (/mac|os x/.test(text)) return 'macos';
    if (/linux|ubuntu|debian/.test(text)) return 'linux';
    if (/android/.test(text)) return 'android';
    if (/ios|iphone|ipad/.test(text)) return 'ios';
    if (/web|browser/.test(text)) return 'web';
    return '';
  };
  class NetumResourceLibrary extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.controller) this.initialize(); });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    async loadSourcePages(signal) {
      const pager = this.querySelector('[data-library-source-pages]');
      if (!pager || this.dataset.sourcesLoaded === 'true') return;
      const loading = this.querySelector('[data-library-loading]');
      loading.hidden = false;
      const current = Number(pager.dataset.currentPage);
      const pages = Array.from({length:Number(pager.dataset.totalPages)}, (_, i) => i + 1).filter(page => page !== current);
      const responses = await Promise.all(pages.map(async page => {
        const url = new URL(location.href);
        url.searchParams.set(pager.dataset.pageParam, String(page));
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, {signal, credentials:'same-origin'});
        if (!response.ok) throw new Error('Resource page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const library = [...doc.querySelectorAll('netum-resource-library')].find(el => el.dataset.sectionId === this.dataset.sectionId);
        if (!library || Number(library.querySelector('[data-library-source-pages]')?.dataset.currentPage) !== page) throw new Error('Unexpected resource page');
        return [...library.querySelectorAll('[data-library-row]')];
      }));
      if (signal.aborted || !this.isConnected) return;
      const rows = [...this.querySelectorAll('[data-library-row]'), ...responses.flat()];
      rows.sort((a,b) => Number(a.dataset.resourcePosition) - Number(b.dataset.resourcePosition));
      this.querySelector('[data-library-items]').replaceChildren(...rows);
      this.dataset.sourcesLoaded = 'true';
      loading.hidden = true;
      pager.hidden = true;
    }
    async initialize() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      try { await this.loadSourcePages(signal); }
      catch (error) {
        if (signal.aborted) return;
        this.querySelector('[data-library-loading]').hidden = true;
        this.querySelector('[data-library-load-error]').hidden = false;
        return;
      }
      if (signal.aborted || !this.isConnected) return;
      const listen = (node, event, callback) => node?.addEventListener(event, callback, { signal: this.controller.signal });
      this.labels = JSON.parse(this.querySelector('[data-library-labels]')?.textContent || '{}');
      this.search = this.querySelector('[data-library-search]');
      this.reset = this.querySelector('[data-library-reset]');
      this.count = this.querySelector('[data-library-count]');
      this.empty = this.querySelector('[data-library-no-results]');
      this.pagination = this.querySelector('[data-library-pagination]');
      this.pageList = this.querySelector('[data-library-pages]');
      this.previous = this.querySelector('[data-library-previous]');
      this.next = this.querySelector('[data-library-next]');
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 12);
      this.currentPage = 1;
      this.facets = new Map([...this.querySelectorAll('[data-facet]')].map(select => [select.dataset.facet, select]));
      this.rows = [...this.querySelectorAll('[data-library-row]')].map(element => {
        const title = element.querySelector('[data-resource-title]')?.textContent.trim() || '';
        const files = [...element.querySelectorAll('[data-file-link]')].map((link, index) => ({
          index, url: link.getAttribute('href'), label: link.dataset.label || link.textContent.trim(),
          language: link.dataset.language || '', platform: platformOf(link.dataset.platform),
          rawPlatform: link.dataset.platform || '', note: link.closest('[data-file-option]')?.querySelector('[data-file-note]'),
        }));
        const row = {
          element, title, files, category: element.dataset.category || '', type: element.dataset.type || '', series: element.dataset.series || '',
          index: normalize(`${element.dataset.search} ${files.map(f => `${f.label} ${f.language} ${f.rawPlatform}`).join(' ')}`),
          select: element.querySelector('[data-download-select]'), download: element.querySelector('[data-selected-download]'),
          note: element.querySelector('[data-selected-note]'), selected: 0,
        };
        row.select?.replaceChildren(...files.map(file => {
          const option = document.createElement('option'); option.value = String(file.index);
          option.textContent = file.label + (file.rawPlatform && !file.label.toLowerCase().includes(file.rawPlatform.toLowerCase()) ? ` · ${file.rawPlatform}` : '');
          return option;
        }));
        listen(row.select, 'change', () => { row.selected = Number(row.select.value); this.updateDownload(row); });
        element.querySelector('[data-file-list]').hidden = true;
        element.querySelector('[data-download-enhanced]').hidden = false;
        return row;
      });
      for (const [name, select] of this.facets) {
        const values = [...new Set(this.rows.flatMap(row => this.valuesFor(row, name)))].filter(Boolean);
        select.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
        for (const value of values) {
          const option = document.createElement('option'); option.value = value;
          option.textContent = this.labels[name]?.[value] || value.replaceAll('_', ' ');
          select.append(option);
        }
        select.closest('[data-facet-field]').hidden = this.dataset.controls !== 'search_filters' || values.length < 2;
        listen(select, 'change', () => { this.currentPage = 1; this.render(); });
      }
      const toolbar = this.querySelector('[data-library-toolbar]');
      if (toolbar) toolbar.hidden = this.dataset.controls === 'none';
      const initialModel = new URLSearchParams(location.search).get('model');
      if (this.search && initialModel && !this.search.value) this.search.value = initialModel;
      listen(this.search, 'input', () => { this.currentPage = 1; this.render(); });
      listen(this.reset, 'click', () => {
        if (this.search) this.search.value = '';
        this.facets.forEach(select => { select.value = ''; });
        this.currentPage = 1; this.render(); this.search?.focus();
      });
      listen(this.pagination, 'click', event => {
        const button = event.target.closest('button'); if (!button || button.disabled) return;
        if (button.hasAttribute('data-library-previous')) this.currentPage--;
        else if (button.hasAttribute('data-library-next')) this.currentPage++;
        else if (button.dataset.page) this.currentPage = Number(button.dataset.page);
        else return;
        this.render();
        this.querySelector('[data-library-items]')?.scrollIntoView?.({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
      const applyAnchor = () => {
        const hash = decodeURIComponent(location.hash.slice(1));
        const anchors = [...this.querySelectorAll('[data-legacy-category]')];
        const legacyCategory = hash.endsWith('__thermal_printer_manual_library') ? 'thermal_printer' : hash.endsWith('__book_scanner_manual_library') ? 'document_camera' : '';
        const anchor = anchors.find(element => element.id === hash) || (legacyCategory && anchors.find(element => element.dataset.legacyCategory === legacyCategory));
        if (anchor && this.facets.has('category')) {
          this.facets.get('category').value = anchor.dataset.legacyCategory; this.currentPage = 1; this.render();
          this.scrollIntoView?.({block:'start'});
        }
      };
      listen(window, 'hashchange', applyAnchor);
      this.render(); applyAnchor();
      this.dataset.ready = 'true';
    }
    valuesFor(row, name) {
      return name === 'platform' || name === 'language' ? row.files.map(file => file[name]).filter(Boolean) : [row[name]];
    }
    fileMatches(file, exclude = '') {
      return ['platform', 'language'].every(name => name === exclude || !this.facets.get(name)?.value || file[name] === this.facets.get(name).value);
    }
    matches(row, exclude = '') {
      const tokens = (this.search?.value || '').trim().split(/\s+/).map(normalize).filter(Boolean);
      if (!tokens.every(token => row.index.includes(token))) return false;
      for (const name of ['category', 'type', 'series']) {
        const value = this.facets.get(name)?.value;
        if (name !== exclude && value && row[name] !== value) return false;
      }
      return row.files.some(file => this.fileMatches(file, exclude));
    }
    updateDownload(row) {
      const available = row.files.filter(file => this.fileMatches(file));
      if (!available.some(file => file.index === row.selected)) row.selected = available[0]?.index ?? -1;
      for (const option of row.select?.options || []) {
        option.disabled = !available.some(file => String(file.index) === option.value);
        option.hidden = option.disabled;
      }
      const selected = row.files.find(file => file.index === row.selected);
      if (!row.download) return;
      row.select.value = String(row.selected);
      row.select.closest('label').hidden = available.length <= 1;
      row.note.replaceChildren();
      if (selected) {
        row.download.href = selected.url;
        row.download.removeAttribute('aria-disabled'); row.download.removeAttribute('tabindex');
        row.download.setAttribute('aria-label', `${this.labels.download}: ${row.title} — ${selected.label}`);
        row.download.textContent = 'Download';
        if (selected.note) row.note.append(...[...selected.note.childNodes].map(node => node.cloneNode(true)));
      } else {
        row.download.removeAttribute('href'); row.download.setAttribute('aria-disabled', 'true'); row.download.tabIndex = -1;
      }
    }
    render() {
      const matches = this.rows.filter(row => this.matches(row));
      const pageCount = Math.ceil(matches.length / this.pageSize);
      this.currentPage = Math.max(1, Math.min(this.currentPage, pageCount || 1));
      const visible = new Set(matches.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize));
      this.rows.forEach(row => { row.element.hidden = !visible.has(row); if (visible.has(row)) this.updateDownload(row); });
      this.facets.forEach((select, name) => {
        for (const option of select.options) {
          option.disabled = Boolean(option.value) && !this.rows.some(row => this.matches(row, name) && (['platform', 'language'].includes(name)
            ? row.files.some(file => file[name] === option.value && this.fileMatches(file, name))
            : row[name] === option.value));
        }
      });
      if (this.count) this.count.textContent = (this.labels.results || '%count% resources').replace('%count%', String(matches.length));
      if (this.empty) this.empty.hidden = matches.length !== 0;
      if (this.reset) this.reset.hidden = !(this.search?.value || [...this.facets.values()].some(select => select.value));
      if (this.pagination) this.pagination.hidden = pageCount <= 1;
      if (this.previous) this.previous.disabled = this.currentPage <= 1;
      if (this.next) this.next.disabled = this.currentPage >= pageCount;
      if (this.pageList) {
        const focusPage = document.activeElement?.dataset.page;
        this.pageList.replaceChildren(...Array.from({length:pageCount}, (_, index) => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'netum-library__page';
          button.dataset.page = String(index + 1); button.textContent = String(index + 1);
          button.setAttribute('aria-label', `${this.labels.page} ${index + 1}`);
          if (index + 1 === this.currentPage) button.setAttribute('aria-current', 'page');
          return button;
        }));
        if (focusPage) this.pageList.querySelector(`[data-page="${Number(focusPage)}"]`)?.focus();
      }
    }
  }
  customElements.define('netum-resource-library', NetumResourceLibrary);
})();
